import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvVideoStateNotice from './OvVideoStateNotice';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoPlacement } from '../../../common/model/dto/video/video-placement';
import { VideoUploadItem } from '../../../context/video-upload.types';
import {
  findPlacement,
  getPlacementNotice,
} from '../../../common/utils/video-placement-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoShownInProps {
  video: AdminVideo;
  placements: VideoPlacement[] | undefined;
  upload?: VideoUploadItem;
  children?: ReactNode;
}

const OvVideoShownIn: FC<OvVideoShownInProps> = ({
  video,
  placements,
  upload,
  children,
}) => {
  const { t } = useTranslation();

  if (!video.placement_key) {
    return <span>{t('videos.placement.notShown')}</span>;
  }

  const placement = placements
    ? findPlacement(placements, video.placement_key)
    : undefined;
  const isRetired = !!placements && !placement;
  const notice = getPlacementNotice(video, upload);

  return (
    <Wrapper>
      {placement && <span>{placement.description}</span>}
      <Key>{video.placement_key}</Key>
      {isRetired && <Secondary>{t('videos.placement.retired')}</Secondary>}
      <Secondary>
        {t('videos.placement.orderValue', { position: video.position })}
      </Secondary>
      {notice && (
        <OvVideoStateNotice
          notice={{ kind: 'placement', notice }}
          variant="compact"
        />
      )}
      {children}
    </Wrapper>
  );
};

export default OvVideoShownIn;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 10rem;
`;

const Key = styled.span`
  font-family: monospace;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
  overflow-wrap: anywhere;
`;

const Secondary = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;
